import React, { useEffect, useState, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineSync,
  AiOutlineCheckCircle,
  AiOutlineClose,
  AiOutlineSearch,
  AiOutlinePlus,
  AiOutlineFileImage,
} from "react-icons/ai";
import authService from "../../services/authService";

// ------ REAL API SECTION ------
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin/documents`;

async function apiFetchCategories() {
  const response = await authService.adminApiCall(`${API_BASE_URL}/categories/`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch categories');
  }
  
  const data = await response.json();
  return data.data || [];
}

async function apiAddCategory(data) {
  const response = await authService.adminApiCall(`${API_BASE_URL}/categories/`, {
    method: 'POST',
    body: JSON.stringify({
      name: data.name,
      description: data.description
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to add category');
  }
  
  return true;
}

async function apiEditCategory({ id, data }) {
  // Ensure ID is always a string (UUID)
  const categoryId = String(id);
  const response = await authService.adminApiCall(`${API_BASE_URL}/categories/${categoryId}/`, {
    method: 'PUT',
    body: JSON.stringify({
      name: data.name,
      description: data.description
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to edit category');
  }
  
  return true;
}

async function apiDeleteCategory(id) {
  // Ensure ID is always a string (UUID)
  const categoryId = String(id);
  const response = await authService.adminApiCall(`${API_BASE_URL}/categories/${categoryId}/`, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete category');
  }
  
  return true;
}

async function apiChangeStatusCategory(id) {
  // Ensure ID is always a string (UUID)
  const categoryId = String(id);
  const response = await authService.adminApiCall(`${API_BASE_URL}/categories/${categoryId}/toggle-status/`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error('Failed to change category status');
  }
  
  return true;
}
// ------ END REAL API SECTION ------

function validateCategory(fields, isEdit = false) {
  let errs = {};
  if (!fields.name?.trim()) errs.name = "Name required";
  if (!fields.description?.trim()) errs.description = "Description required";
  return errs;
}

export default function Category() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination/Search
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  // Dialogs
  const [editRow, setEditRow] = useState(null);
  const [editTouched, setEditTouched] = useState({});
  const [editVals, setEditVals] = useState({});
  const [editErrs, setEditErrs] = useState({});
  const [addDialog, setAddDialog] = useState(false);
  const [addTouched, setAddTouched] = useState({});
  const [addVals, setAddVals] = useState({});
  const [addErrs, setAddErrs] = useState({});
  const [deleteRow, setDeleteRow] = useState(null);

  // Success tick
  const [successMsg, setSuccessMsg] = useState("");
  const editFileRef = useRef();
  const addFileRef = useRef();

  useEffect(() => { fetchAll(); }, []);
  async function fetchAll() {
    setLoading(true);
    setData(await apiFetchCategories());
    setLoading(false);
  }

  const filteredData = useMemo(() => {
    try {
      if (!data || !Array.isArray(data)) return [];
      const q = search.trim().toLowerCase();
      if (!q) return data;
      return data.filter(
        d => {
          if (!d) return false;
          const nameMatch = d.name && typeof d.name === 'string' && d.name.toLowerCase().includes(q);
          const descMatch = d.description && typeof d.description === 'string' && d.description.toLowerCase().includes(q);
          return nameMatch || descMatch;
        }
      );
    } catch (error) {
      console.error('Error filtering categories:', error);
      return data || [];
    }
  }, [data, search]);

  const columns = useMemo(() => [
    {
      name: "S.No",
      width: "60px",
      cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
      style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
      sortable: false,
      center: true,
    },
    {
      name: "Name",
      selector: row => row.name,
      sortable: true,
      minWidth: "110px",
      style: { fontWeight: 600, whiteSpace: "nowrap" }
    },
    {
      name: "Description",
      selector: row => row.description,
      sortable: false,
      minWidth: "230px",
      cell: row => (
        <span style={{
          fontSize: 14, color: "#4e5864", maxWidth: 480,
          whiteSpace: "pre-line", display: "inline-block", wordBreak: "break-word", lineHeight: "1.55"
        }}>
          {row.description || 'N/A'}
        </span>
      ),
    },
    {
      name: "Status",
      selector: row => row.is_active,
      minWidth: "100px",
      style: { textAlign: "center", whiteSpace: "nowrap" },
      cell: row => (
        <span style={{
          borderRadius: 14,
          padding: "5px 17px",
          fontWeight: 700,
          color: "#fff",
          fontSize: 14,
          background: row.is_active ? "#21bb55" : "#dc2626",
          boxShadow: row.is_active ? "0 2px 8px #21bb551a" : "0 2px 9px #dc262611",
          letterSpacing: ".02em",
          minWidth: 70,
          userSelect: "none"
        }}>
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
      center: true,
      sortable: false,
    },
    {
      name: "Actions",
      minWidth: "130px",
      style: { whiteSpace: "nowrap", textAlign: "center" },
      cell: row => (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, whiteSpace: "nowrap" }}>
          <button onClick={() => openEdit(row)} style={iconBtn('#3193eb')} title="Edit"><AiOutlineEdit size={18} /></button>
          <button onClick={() => openDelete(row)} style={iconBtn('#dc2626')} title="Delete"><AiOutlineDelete size={18} /></button>
          <button onClick={() => handleChangeStatus(row)} style={iconBtn('#2979f2')} title="Change Status"><AiOutlineSync size={18} /></button>
        </div>
      ),
      ignoreRowClick: true,
      center: true,
      sortable: false,
    }
  ], [currentPage, perPage]);

  // Add/ Edit dialog logic/handlers
  function openAdd() {
    setAddDialog(true);
    setAddVals({ name: "", description: "" });
    setAddErrs({});
    setAddTouched({ name: true, description: true }); // Mark as touched to show validation immediately
  }
  function handleAddInput(e) {
    const { name, value } = e.target;
    setAddVals(v => ({ ...v, [name]: value }));
    setAddTouched(t => ({ ...t, [name]: true }));
    setAddErrs(validateCategory({ ...addVals, [name]: value }));
  }
  async function handleSaveAdd(e) {
    e.preventDefault();
    const errs = validateCategory(addVals);
    setAddErrs(errs);
    setAddTouched({ name: true, description: true });
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      await apiAddCategory(addVals);
      setData(await apiFetchCategories());
      setAddDialog(false);
      setSuccessMsg("Saved successfully!");
    } catch (error) {
      setSuccessMsg("Error: " + error.message);
    }
    setLoading(false);
  }
  function openEdit(row) {
    setEditRow(row);
    setEditVals({ name: row.name, description: row.description });
    setEditErrs({});
    setEditTouched({ name: true, description: true }); // Mark as touched to show validation immediately
  }
  function handleEditInput(e) {
    const { name, value } = e.target;
    setEditVals(v => ({ ...v, [name]: value }));
    setEditTouched(t => ({ ...t, [name]: true }));
    setEditErrs(validateCategory({ ...editVals, [name]: value }, true));
  }
  async function handleSaveEdit(e) {
    e.preventDefault();
    const errs = validateCategory(editVals, true);
    setEditErrs(errs);
    setEditTouched({ name: true, description: true });
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      await apiEditCategory({ id: editRow.id, data: editVals });
      setData(await apiFetchCategories());
      setEditRow(null);
      setSuccessMsg("Saved successfully!");
    } catch (error) {
      setSuccessMsg("Error: " + error.message);
    }
    setLoading(false);
  }
  function openDelete(row) { setDeleteRow(row); }
  async function handleDeleteYes() {
    setLoading(true);
    try {
      await apiDeleteCategory(deleteRow.id);
      setData(await apiFetchCategories());
      setDeleteRow(null);
      setSuccessMsg("Deleted successfully!");
    } catch (error) {
      setSuccessMsg("Error: " + error.message);
    }
    setLoading(false);
  }
  async function handleChangeStatus(row) {
    setLoading(true);
    try {
      await apiChangeStatusCategory(row.id);
      setData(await apiFetchCategories());
    } catch (error) {
      setSuccessMsg("Error: " + error.message);
    }
    setLoading(false);
  }
  function closeSuccess() { setSuccessMsg(""); }

  function handlePageChange(page) { setCurrentPage(page); }
  function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); setCurrentPage(page); }

  // --- Only the relevant top bar changes below:

  function SearchBar() {
    return (
      <div style={{
        display: "flex", alignItems: "center", borderRadius: 7, border: "1.5px solid #aab6fe", background: "#f0f7ff",
        maxWidth: 280, width: "100%", padding: "2px 9px"
      }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name or description"
          style={{
            border: "none", flexGrow: 1, height: 30, fontSize: 14, outline: "none", background: "transparent"
          }} aria-label="Search categories"
        />
        <AiOutlineSearch size={20} style={{ color: "#2979f2" }} />
      </div>
    );
  }

  // Reuse helpers from previous completions (FormField, iconBtn, modalBtn, Modal, SuccessModal)

  return (
    <section style={{ //maxWidth: 1320, margin: "0 auto", 
    padding: 22 }}>
      {/* ---HEADING + Add CATEGORY BUTTON IN ONE LINE --- */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "nowrap",
          gap: 20,
          overflowX: "auto",
        }}
      >
        <h1 style={{ fontWeight: 800, fontSize: 27, color: "#215ea7", display: "flex", alignItems: "center", gap: 11, whiteSpace: "nowrap" }}>
          <AiOutlineFileImage size={33} />
          Category Management
        </h1>
        <div style={{ display: "flex", gap: 13, alignItems: "center" }}>
          <SearchBar />
          <button
            onClick={openAdd}
            style={{
              background: "linear-gradient(90deg,#217eed,#49dfa9 100%)",
              color: "#fff",
              borderRadius: 9,
              fontWeight: 700,
              fontSize: 16,
              padding: "8px 23px",
              border: "none",
              display: "flex", alignItems: "center", gap: 8,
              cursor: "pointer", boxShadow: "0 2px 11px #49dfa955", whiteSpace: "nowrap"
            }}
          >
            <AiOutlinePlus size={20} />
            Add Category
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        highlightOnHover
        striped
        pagination
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationDefaultPage={currentPage}
        progressPending={loading}
        noHeader
        responsive
        persistTableHead
        customStyles={{
          table: { style: { width: "100%" } },
          rows: { style: { fontSize: 15, background: "#f8fafc", whiteSpace: "nowrap" } },
          headRow: { style: { background: "#eceffd", color: "#433ca", fontWeight: 700, fontSize: 15 } },
          headCells: { style: { borderBottom: "1.6px solid #a5a6ff", padding: "8px 10px" } },
          cells: { style: { padding: "8px 10px", verticalAlign: "middle" } },
          pagination: { style: { borderTop: "1px solid #aab6fe", color: "#433ca", fontWeight: 600 } }
        }}
      />

      {/* --- ADD Dialog --- */}
      {addDialog && (
        <Modal onClose={() => setAddDialog(false)} title="Add Category">
          <form onSubmit={handleSaveAdd} style={{ display: "flex", flexDirection: "column", gap: 24, minWidth: 420 }}>
            <FormField label="Name" name="name" value={addVals.name} onChange={handleAddInput} touched={addTouched.name} error={addErrs.name} />
            <FormField label="Description" name="description" value={addVals.description} onChange={handleAddInput} textarea touched={addTouched.description} error={addErrs.description} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 14 }}>
              <button type="button" onClick={() => setAddDialog(false)} style={modalBtn("#b7bafe", "#254")}>Cancel</button>
              <button
                type="submit"
                style={{
                  ...modalBtn("#215ea7", "#fff"),
                  ...(Object.keys(validateCategory(addVals)).length > 0 ? {
                    background: "#ccc",
                    color: "#666",
                    cursor: "not-allowed",
                    opacity: 0.6
                  } : {})
                }}
                disabled={Object.keys(validateCategory(addVals)).length > 0}
              >Add</button>
            </div>
          </form>
        </Modal>
      )}

      {/* --- EDIT Dialog --- */}
      {editRow && (
        <Modal onClose={() => setEditRow(null)} title="Edit Category">
          <form onSubmit={handleSaveEdit} style={{ display: "flex", flexDirection: "column", gap: 24, minWidth: 420 }}>
            <FormField label="Name" name="name" value={editVals.name} onChange={handleEditInput} touched={editTouched.name} error={editErrs.name} />
            <FormField label="Description" name="description" value={editVals.description} onChange={handleEditInput} textarea touched={editTouched.description} error={editErrs.description} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 14 }}>
              <button type="button" onClick={() => setEditRow(null)} style={modalBtn("#cad2ff", "#254")}>Cancel</button>
              <button
                type="submit"
                style={{
                  ...modalBtn("#215ea7", "#fff"),
                  ...(Object.keys(validateCategory(editVals, true)).length > 0 ? {
                    background: "#ccc",
                    color: "#666",
                    cursor: "not-allowed",
                    opacity: 0.6
                  } : {})
                }}
                disabled={Object.keys(validateCategory(editVals, true)).length > 0}
              >Save</button>
            </div>
          </form>
        </Modal>
      )}

      {/* --- DELETE Dialog --- */}
      {deleteRow && (
        <Modal onClose={() => setDeleteRow(null)} title="Delete Category">
          <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 18 }}>
            Are you sure you want to delete <b>{deleteRow.name}</b>?
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 14 }}>
            <button onClick={() => setDeleteRow(null)} style={modalBtn("#cad2ff", "#254")}>Cancel</button>
            <button onClick={handleDeleteYes} style={modalBtn("#dc2626", "#fff")}>Delete</button>
          </div>
        </Modal>
      )}

      {/* --- SUCCESS MODAL --- */}
      {successMsg && (
        <SuccessModal message={successMsg} onClose={closeSuccess} />
      )}
      <style>
        {`
          @media (max-width: 850px) {
            .rdt_TableCol, .rdt_TableCell {
              padding-left: 4px !important;
              padding-right: 4px !important;
              font-size: 13px;
            }
          }
        `}
      </style>
    </section>
  );
}

// Helper components (unchanged)
function FormField({ label, name, value, onChange, touched, error, inputProps, textarea }) {
  return (
    <div>
      <label style={labelStyle}>
        {label} <span style={{ color: "#dd362b" }}>*</span>
      </label>
      {textarea
        ? <textarea name={name} value={value} onChange={onChange} rows={3} style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} required />
        : <input name={name} type="text" value={value} onChange={onChange} style={inputStyle} required {...(inputProps || {})} />
      }
      {touched && error && (
        <span style={errStyle}>{error}</span>
      )}
    </div>
  );
}
function FileField({ mode, value, onChange, touched, error }) {
  return (
    <div>
      <label style={labelStyle}>
        Image <span style={{ color: "#dd362b" }}>*</span>
      </label>
      <input
        type="file"
        name="image"
        accept=".png,.jpeg,.jpg,image/png,image/jpeg"
        onChange={onChange}
        style={{ ...inputStyle, padding: 6, fontSize: 15, lineHeight: 1.1 }}
        required={mode === "add"}
      />
      <span style={{ display: "block", fontSize: 13.5, color: "#2750a6", marginTop: 1 }}>
        Only .png, .jpeg. Max 5MB.
      </span>
      {value && (typeof value === "string" || (value && value.name)) && (
        <div style={{ margin: "7px 0 5px 0", display: "flex", alignItems: "center", gap: 10 }}>
          {(typeof value === "string"
            ? <img src={value} alt="selected" style={{ width: 45, height: 36, borderRadius: 7, objectFit: "cover", border: "1px solid #cbd5e0" }} />
            : <span><AiOutlineFileImage size={27} /></span>)
          }
          <span style={{ color: "#226", fontSize: 13 }}>
            {(typeof value === "string") ? "Already selected" : value && value.name}
          </span>
        </div>
      )}
      {touched && error && <span style={errStyle}>{error}</span>}
    </div>
  );
}
function iconBtn(bg) {
  return {
    background: bg,
    color: "#fff",
    border: "none",
    borderRadius: 7,
    padding: 6,
    margin: 0,
    cursor: "pointer",
    width: 32, height: 32,
    display: "flex", justifyContent: "center", alignItems: "center"
  };
}
function modalBtn(bg, color, px=16, py=8) {
  return {
    padding: `${py}px ${px}px`,
    background: bg,
    color: color,
    border: "none",
    borderRadius: 7,
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    outline: "none"
  };
}
const labelStyle = { color: "#215ea7", fontWeight: 700, fontSize: 15, marginBottom: 8, display: "block" };
const inputStyle = {
  padding: 11, fontSize: 16, borderRadius: 7, border: "1.6px solid #aec0fa",
  outline: "none", width: "100%", fontWeight: 600, color: "#243567", letterSpacing: ".04em", background: "#f6f9ff"
};
const errStyle = { color: "#dc2626", fontWeight: 600, fontSize: 13, marginTop: 2 };

function Modal({ onClose, title, children }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.15)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10001
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#f9fcfe",
          borderRadius: 15,
          position: "relative",
          padding: "39px 39px 23px 39px",
          minWidth: 350,
          maxWidth: 490,
          width: "99vw",
          boxShadow: "0 10px 40px #a5b4fc33"
        }}
      >
        <h3 id="modalTitle" style={{ fontWeight: 800, color: "#215ea7", margin: 0, marginBottom: 18, fontSize: 22, letterSpacing: ".02em" }}>
          {title}
        </h3>
        {children}
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 14, background: "transparent",
            border: "none", fontSize: 23, cursor: "pointer", color: "#666", userSelect: "none",
          }}
        >
          <AiOutlineClose />
        </button>
      </div>
    </div>
  );
}
function SuccessModal({ message, onClose }) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.13)", zIndex: 10002,
        display: "flex", justifyContent: "center", alignItems: "center"
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fafff9",
          padding: "36px 30px 26px 30px",
          borderRadius: 16,
          textAlign: "center",
          boxShadow: "0 8px 36px #53ec8860",
          color: "#11602c"
        }}>
        <AiOutlineCheckCircle
          size={62}
          color="#17bb5f"
          style={{ marginBottom: 13, animation: "pop-tick .33s cubic-bezier(.19,.7,.68,1.38)" }}
        />
        <div style={{ fontWeight: 800, fontSize: 21, marginBottom: 2 }}>{message}</div>
        <button onClick={onClose} style={modalBtn("#17bb5f", "#fff", 17, 10)}>Close</button>
        <style>{`
          @keyframes pop-tick {
            0% { opacity: 0; transform: scale(0.46);}
            67% { opacity: 1; transform: scale(1.14);}
            100% { opacity: 1; transform: scale(1);}
          }
        `}</style>
      </div>
    </div>
  );
}
